import {
	CollectionModelFieldSubFormInstance,
	DDSFieldIds,
	FormModel,
	FormModelField,
	FormModelFieldNestedList,
	FormModelFieldSelectList,
	FormModelFieldSubForm,
	IkePoleForemanFieldIds,
	IPFSpanType,
	type IPoleForm,
	type ISpan,
	LegacyPoleForemanFieldIds,
	PLASpanAdaptor,
	PoleFieldIds,
	SpanType,
} from '..';

export class PLAForm implements IPoleForm {
	static isPLAForm(form: FormModel): boolean {
		return form.metadata.isPLAForm();
	}

	constructor(public form: FormModel) {
		if (!PLAForm.isPLAForm(form)) {
			throw new Error('Form is not a PLA form');
		}
	}

	public get type() {
		if (this.form.metadata.options.spidacalc) {
			return 'spidacalc';
		}
		if (this.form.metadata.options.dds) {
			return 'dds';
		}
		if (this.form.metadata.options.ikePoleForeman) {
			return 'ikePoleForeman';
		}
		if (this.form.metadata.options.ocalc) {
			return 'ocalc';
		}
		if (this.form.metadata.options.poleforeman) {
			return 'poleforeman';
		}
		throw new Error('Form is not a PLA form');
	}

	private getField<T extends FormModelField>(
		subformInstance: FormModelFieldSubForm,
		ikePoleFieldIdsId: string,
		ikePoleForemanFieldId: string | null
	): T | null {
		switch (this.type) {
			case 'dds':
			case 'ocalc':
			case 'spidacalc':
			case 'poleforeman':
				return subformInstance.getOptionalField<T>(ikePoleFieldIdsId);
			case 'ikePoleForeman':
				if (!ikePoleForemanFieldId) {
					return null;
				}
				return subformInstance.getOptionalField<T>(ikePoleForemanFieldId);
			default:
				throw new Error(`Unrecognized form type: ${this.type}`);
		}
	}
	public get statusField(): FormModelFieldNestedList | null {
		return this.getField(this.form, PoleFieldIds.STATUS, IkePoleForemanFieldIds.STATUS);
	}

	public get sequenceField(): null {
		return null;
	}

	public get idField(): FormModelField {
		return this.getField(this.form, PoleFieldIds.POLE_ID, IkePoleForemanFieldIds.ID)!;
	}

	public get tagPhotosField(): FormModelField {
		return this.getField(this.form, PoleFieldIds.IMAGES, IkePoleForemanFieldIds.TAG_PHOTOS)!;
	}

	public get typeField(): FormModelFieldNestedList | null {
		return this.getField(this.form, PoleFieldIds.POLE_TYPE, IkePoleForemanFieldIds.TYPE);
	}

	public get ownerField(): FormModelFieldNestedList | null {
		return this.getField(this.form, PoleFieldIds.POLE_OWNER, IkePoleForemanFieldIds.OWNER);
	}

	public get circumferenceField(): FormModelField | null {
		return this.getField(this.form, PoleFieldIds.CIRCUMFERENCE, null);
	}

	public get tipField(): FormModelField {
		return this.getField(this.form, PoleFieldIds.AGL, IkePoleForemanFieldIds.TIP)!;
	}

	public get lowPowerAttachmentField(): null {
		return null;
	}

	public get locationField(): FormModelField {
		return this.getField(this.form, PoleFieldIds.GEOGRAPHIC_COORDINATE, IkePoleForemanFieldIds.LOCATION)!;
	}

	public get streetAddressField(): null {
		return null;
	}

	public get noteField(): FormModelField | null {
		return this.getField(this.form, PoleFieldIds.NOTE, IkePoleForemanFieldIds.NOTE);
	}

	public get ikePhotoField(): FormModelField {
		return this.getField(this.form, PoleFieldIds.IKE_PHOTO, IkePoleForemanFieldIds.IKE_PHOTO)!;
	}

	public get lowPowerToCommField(): null {
		return null;
	}

	public get equipmentField(): FormModelFieldSubForm {
		return this.getField(this.form, PoleFieldIds.EQUIPMENT.FORM, IkePoleForemanFieldIds.EQUIPMENT)!;
	}

	public get anchorField(): FormModelFieldSubForm {
		return this.getField(this.form, PoleFieldIds.ANCHOR.FORM, IkePoleForemanFieldIds.ANCHOR)!;
	}

	public get spanField() {
		return this.getField<FormModelFieldSubForm>(this.form, PoleFieldIds.SPAN.FORM, IkePoleForemanFieldIds.SPAN)!;
	}

	public get spanTypeField() {
		return this.getField<FormModelFieldSelectList>(
			this.spanField,
			PoleFieldIds.SPAN.TYPE,
			IkePoleForemanFieldIds.SPAN_TYPE
		)!;
	}

	get spanTypeMapping(): Record<string, string> {
		if (this.type === 'ikePoleForeman') {
			return {
				[IPFSpanType.FORE_SPAN]: IPFSpanType.BACK_SPAN,
				[IPFSpanType.BACK_SPAN]: IPFSpanType.FORE_SPAN,
				[IPFSpanType.OTHER]: IPFSpanType.OTHER,
			};
		} else {
			return {
				[SpanType.NextPole]: SpanType.PreviousPole,
				[SpanType.PreviousPole]: SpanType.NextPole,
				[SpanType.Other]: SpanType.Other,
			};
		}
	}

	public get spanVectorField() {
		return this.getField<FormModelFieldSelectList>(
			this.spanField,
			PoleFieldIds.SPAN.LENGTH,
			IkePoleForemanFieldIds.SPAN_LENGTH
		)!;
	}

	public get spanMidSpanIkePhoto() {
		return this.getField(
			this.spanField,
			PoleFieldIds.SPAN.MID_SPAN_PHOTO,
			IkePoleForemanFieldIds.SPAN_MID_SPAN_IKE_PHOTO
		)!;
	}

	get spanAttachmentHeightFields(): FormModelField[] {
		const fieldIds: string[] = [];

		// IPF has a completely different structure with different fields
		if (this.type === 'ikePoleForeman') {
			fieldIds.push(
				IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_PRIMARY_PHASE_A_HEIGHT,
				IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_PRIMARY_PHASE_B_HEIGHT,
				IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_PRIMARY_PHASE_C_HEIGHT,
				IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_NEUTRAL_HEIGHT,
				IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_SECONDARY_PHASE_A_HEIGHT,
				IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_SECONDARY_PHASE_B_HEIGHT,
				IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_SECONDARY_PHASE_C_HEIGHT,
				IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_SHIELD_WIRE_HEIGHT,

				IkePoleForemanFieldIds.SPAN_CIRCUIT_AERIAL_AERIAL_ATTACHMENT_HEIGHT,
				IkePoleForemanFieldIds.SPAN_CIRCUIT_AERIAL_NEUTRAL_HEIGHT,
				IkePoleForemanFieldIds.SPAN_CIRCUIT_AERIAL_SECONDARY_PHASE_A_HEIGHT,
				IkePoleForemanFieldIds.SPAN_CIRCUIT_AERIAL_SECONDARY_PHASE_B_HEIGHT,
				IkePoleForemanFieldIds.SPAN_CIRCUIT_AERIAL_SECONDARY_PHASE_C_HEIGHT,

				IkePoleForemanFieldIds.SPAN_CIRCUIT_SPACER_SPACER_ATTACHMENT_HEIGHT,
				IkePoleForemanFieldIds.SPAN_CIRCUIT_SPACER_NEUTRAL_HEIGHT,
				IkePoleForemanFieldIds.SPAN_CIRCUIT_SPACER_SECONDARY_PHASE_A_HEIGHT,
				IkePoleForemanFieldIds.SPAN_CIRCUIT_SPACER_SECONDARY_PHASE_B_HEIGHT,
				IkePoleForemanFieldIds.SPAN_CIRCUIT_SPACER_SECONDARY_PHASE_C_HEIGHT,

				IkePoleForemanFieldIds.SPAN_CIRCUIT_FOSC_HEIGHT,

				IkePoleForemanFieldIds.SPAN_GUY_ATTACHMENT_HEIGHT,

				IkePoleForemanFieldIds.SPAN_COMMUNICATION_ATTACHMENT_HEIGHT
			);
		} else {
			// The following are mostly standard across all other PLA forms
			fieldIds.push(
				PoleFieldIds.WIRE.ATTACHMENT_HEIGHT,
				PoleFieldIds.SPAN_GUY.ATTACHMENT_HEIGHT,
				PoleFieldIds.SPAN_GUY.MID_SPAN_HEIGHT,
				PoleFieldIds.SPAN_GUY.HEIGHT_AT_WEP
			);

			// DDS has additional field which the other old PLA forms don't have
			if (this.type === 'dds') {
				fieldIds.push(DDSFieldIds.JOINT_USE_CABLE.ATTACHMENT_HEIGHT);
			}

			// Legacy poleforeman has additional field which the other old PLA forms don't have
			if (this.type === 'poleforeman') {
				fieldIds.push(
					LegacyPoleForemanFieldIds.PHASE_A_HEIGHT,
					LegacyPoleForemanFieldIds.PHASE_B_HEIGHT,
					LegacyPoleForemanFieldIds.PHASE_C_HEIGHT
				);
			}
		}

		// Use findField to burrow into nested lists and to safely handle missing fields, filter out nulls
		return fieldIds.map((id) => this.spanField.findField(id)).filter((field): field is FormModelField => field != null);
	}

	enableSpanSubform() {
		this.spanField.Mutator.span.enable();
	}

	spanValue(instance: CollectionModelFieldSubFormInstance): ISpan {
		return new PLASpanAdaptor(instance, this);
	}
}
